import { BLANK_TYPE, BlankItem } from '@doltech/ui/lib/figma/BlankEditor/type';
import { withBlank } from '@doltech/ui/lib/figma/BlankEditor/withBlank';
import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import { useSideToolbarPlugins } from '@doltech/ui/lib/figma/DolEditor/toolbars/sidebar/defaults';
import { QuestionV2 } from '@doltech/ui/lib/figma/Question/QuestionV2';
import { ToolbarDoingTestV2 } from '@doltech/ui/lib/figma/ToolbarDoingTestV2/ToolbarDoingTestV2';
import { numberToABC } from '@doltech/utils/lib/text';
import { Popover } from 'antd';
import cl from 'classnames';
import { get, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Node } from 'slate';
import styled from 'styled-components';

interface DropDownAnswerQuestionProps {
  no: number;
  options: any;
  value: Node[];
  blanks: BlankItem[];
  userAnswers: any;
  onSelectAnswer: (answer) => void;
  onEditorChange?: (value: string) => void;
}

const DropDownAnswerQuestion = observer((props: DropDownAnswerQuestionProps) => {
  const { value, no, blanks, userAnswers, options, onSelectAnswer, onEditorChange, ...rest } =
    props;
  const { plugins, editorChains } = useSideToolbarPlugins({ extraEditorChains: [withBlank] });
  return (
    <EditableContainer>
      <DolEditor
        readOnly
        withToolbar={<ToolbarDoingTestV2 />}
        onEditorChange={(changeValue, _) => {
          onEditorChange?.(JSON.stringify(changeValue));
        }}
        plugins={plugins}
        editorChains={editorChains}
        initialValue={value}
        renderElementDeps={[blanks]}
        renderElement={[
          (renderProps) => {
            const { element } = renderProps;
            const blank = (blanks || []).find((b) => b.id === element.id);
            if (blank && element.type === BLANK_TYPE) {
              return (
                <Element
                  userAnswers={userAnswers}
                  blanks={blanks}
                  no={no}
                  options={options}
                  onSelectAnswer={onSelectAnswer}
                  {...renderProps}
                  {...rest}
                />
              );
            }
            return undefined;
          },
        ]}
        placeholder="Start the sentence"
      />
    </EditableContainer>
  );
});

interface ElementProps {
  options: any;
  attributes: any;
  children?: any;
  element: any;
  no: any;
  blanks: any;
  userAnswers: any;
  onSelectAnswer: any;
}

const Element = observer((props: ElementProps) => {
  const { options, attributes, children, element, no, blanks, userAnswers, onSelectAnswer } = props;

  const [isDropDownShown, setIsDropDownShown] = React.useState(false);
  const [activeBlankId, setActiveBlankId] = React.useState(undefined);
  const blank = (blanks || []).find((b) => b.id === element.id);

  const optionNo = get(
    (userAnswers || []).find((item) => item.blankId === blank?.id),
    'userAnswerKey',
    undefined
  );
  const value = get(
    (userAnswers || []).find((item) => item.blankId === blank?.id),
    'userAnswer',
    ''
  );
  const blankActive = activeBlankId === blank?.id;

  return (
    <BlankContainer
      onClick={(e) => {
        e.stopPropagation();
        setActiveBlankId(blank.id);
        setIsDropDownShown(true);
      }}
      style={{ userSelect: 'none' }}
      {...attributes}
    >
      <QuestionNoWrapper>
        <QuestionV2
          no={no + blank.key}
          id={`question-no-${no}`}
          marginRight={0}
          className={cl('question-no', QuestionDisplayMode.QUESTION_DO_TEST, {
            active: blankActive,
          })}
        />
        <CircleText
          displayMode={QuestionDisplayMode.QUESTION_OPTION_DASHED}
          circleText={!isNil(optionNo) ? numberToABC(optionNo + 1) : ''}
          text=""
          style={{ marginLeft: 8 }}
          active={blankActive}
          selected={value}
        />
      </QuestionNoWrapper>
      <Popover
        overlayClassName="dol-popover"
        trigger={['click']}
        visible={isDropDownShown}
        onVisibleChange={setIsDropDownShown}
        title={null}
        placement="bottomLeft"
        zIndex={1000}
        content={
          <AnswersDropDown contentEditable={false}>
            <OptionsContainer>
              {options.map((option) => (
                <OptionItem
                  key={option.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectAnswer({
                      blankId: blank.id,
                      key: option.key,
                      text: option.value,
                    });
                    setIsDropDownShown(false);
                  }}
                >
                  <CircleText
                    displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
                    circleText={numberToABC(option.key + 1)}
                    text={option.value}
                  />
                </OptionItem>
              ))}
            </OptionsContainer>
          </AnswersDropDown>
        }
      >
        <Typography.Text
          variant="regular/14"
          className={cl('answer-text', { active: blankActive, selected: value })}
          color="gray80"
        >
          {value || 'bấm để chọn đáp án'}
        </Typography.Text>
      </Popover>
      {children}
    </BlankContainer>
  );
});

const EditableContainer = styled.div``;

const BlankContainer = styled.span`
  display: inline;
  margin: 0 12px;
  position: relative;
  height: 30px;
  align-items: center;
  cursor: pointer;
  .question-circle {
    margin-right: 8px;
  }
  && {
    .answer-text {
      &.active {
        color: ${colorsV2.blue40};
      }
      &.selected {
        color: ${colorsV2.blue100};
      }
    }
  }
`;

const QuestionNoWrapper = styled.div`
  display: inline-flex;
`;

const AnswersDropDown = styled.div<any>`
  width: 264px;
  max-height: 224px;
  background-color: ${colorsV2.white100};
  box-shadow: 0 16px 24px ${colorsV2.blackShadow6}, 0 2px 6px ${colorsV2.blackShadow4},
    0 0 1px ${colorsV2.blackShadow4};
  border-radius: 8px;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
`;

const OptionsContainer = styled.div`
  height: 100%;
  overflow-y: overlay;
  padding: 0 14px;
  &::-webkit-scrollbar {
    width: 11px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${colorsV2.black5};
    border-radius: 20px;
    border-left: 4px solid ${colorsV2.white100};
    border-right: 4px solid ${colorsV2.white100};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colorsV2.black80};
    border-radius: 20px;
    border-left: 4px solid ${colorsV2.white100};
    border-right: 4px solid ${colorsV2.white100};
  }
`;

const OptionItem = styled.div`
  border: 1px solid transparent;
  border-radius: 16px;
  padding: 4px;
  &:not(first-child) {
    margin-top: 8px;
  }
  &:hover {
    border: 1px solid ${colorsV2.gray20};
    box-shadow: 0 4px 8px ${colorsV2.blackShadow4}, 0 0 2px ${colorsV2.blackShadow6},
      0 0 1px ${colorsV2.blackShadow4};
  }
`;

export default React.memo(DropDownAnswerQuestion);
